import axios, { AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import { debug } from '../utils/debug';
import { getValidAuthData } from '../utils/authUtils';
import { isTelegramWebApp, isMaxWebApp, readMaxInitDataRaw, detectPlatform } from '../utils/platform';
import type {
  User,
  OnboardingStatus,
  OnboardingStatusResponse,
  OnboardingStepData,
  UserPreferences,
  ApiResponse,
  ApiError,
  AuthVerifyResponse,
  NFTItem,
  NFTDetailItem,
  PaymentInitResponse,
  Transaction,
  PushSettings,
  MyNFTsResponse,
  SoldNFTsResponse,
  UserNFTDetail,
  DropCurrentResponse,
  MyDropsResponse,
  TierInfoResponse,
  NFTOwnershipHistoryResponse,
  PaymentStatusResponse,
  NftPurchaseResponse,
  PurchaseTokenStatus,
  NftPurchaseItem,
  PaginatedResponse,
  ClaimedPrize,
  ClaimPrizeResponse,
  PrizeStatusResponse,
  SupportTicket,
  SupportTicketDetail,
  SupportMessage,
  FaqItem,
  PenkiTransaction,
  PenkiClaimStatus,
  PenkiClaimResult,
  PenkiRateRow,
  ShopProduct,
  ShopPurchase,
} from '../types';

/** Default axios timeout is too low for eKazna /pay and paymentInfo when the provider is slow. */
const PAYMENT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * Global handler invoked when backend responds with 412 `user_unverified` —
 * i.e. the Telegram/Max actor is authenticated but has no linked SBP yet.
 * Set from <SbpGate /> at app mount; reset to null on unmount.
 */
type SbpGateHandler = () => void;
let sbpGateHandler: SbpGateHandler | null = null;

export function setSbpGateHandler(handler: SbpGateHandler | null): void {
  sbpGateHandler = handler;
}

/**
 * Global handler invoked when backend responds with 423 `access_restricted` —
 * i.e. the user is under a temporary fraud ban set from the admin panel.
 * Set from <AccessRestrictedGate /> at app mount; reset to null on unmount.
 */
type AccessRestrictedHandler = () => void;
let accessRestrictedHandler: AccessRestrictedHandler | null = null;

export function setAccessRestrictedHandler(handler: AccessRestrictedHandler | null): void {
  accessRestrictedHandler = handler;
}

/**
 * Fire-and-forget beacon for request failures that never produced an HTTP
 * response (network drops / timeouts). These are invisible to the backend, so
 * we report them to a public endpoint that logs them into /admin/error-logs.
 * Uses sendBeacon so it survives the Mini App being closed.
 */
function sendClientErrorBeacon(error: AxiosError): void {
  try {
    const url = error.config?.url ?? '';
    // Never beacon about the beacon endpoint itself (avoids feedback loops).
    if (url.includes('/client-error')) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;

    let tgId: number | null = null;
    try {
      tgId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id ?? null;
    } catch {
      // ignore
    }

    const payload = {
      type: error.code === 'ECONNABORTED' ? 'timeout' : 'network',
      code: error.code ?? null,
      status: null,
      method: error.config?.method ?? null,
      url,
      message: error.message ?? null,
      platform: detectPlatform(),
      tg_id: tgId,
    };

    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon('/api/client-error', blob);
  } catch {
    // never let diagnostics throw into the request flow
  }
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api', // Use relative path - will go through Vite proxy
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Request interceptor: attach Telegram init data, Max init data, or Login Widget params
    this.client.interceptors.request.use((config) => {
      if (isMaxWebApp()) {
        const maxInitData = window.WebApp?.initData || readMaxInitDataRaw();
        if (maxInitData) {
          config.headers['X-Max-Init-Data'] = maxInitData;
          return config;
        }
      }

      if (isTelegramWebApp()) {
        config.headers['X-Telegram-Init-Data'] = window.Telegram!.WebApp!.initData;
        return config;
      }

      // Standalone web: send Login Widget auth data as query params with every request
      const authData = getValidAuthData();
      if (authData) {
        config.params = config.params || {};
        Object.entries(authData).forEach(([key, value]) => {
          if (value !== undefined) {
            config.params![key] = value;
          }
        });
      }
      return config;
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        debug.error('❌ FRONTEND DEBUG - API Error:', {
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
        });

        // No HTTP response = network drop or timeout. The backend never saw this
        // request, so report it via a beacon for /admin/error-logs visibility.
        if (!error.response) {
          sendClientErrorBeacon(error);
        }

        // On 401: clear desktop auth data and redirect to login (only for standalone web,
        // not when we're inside a messenger WebApp — restart fixes those).
        if (error.response?.status === 401) {
          localStorage.removeItem('telegram_login_widget_data');
          // Guard against the first-paint race inside a WebApp: if the current location.hash
          // still carries Telegram/Max init data but the SDK hasn't populated globals yet,
          // isTelegramWebApp()/isMaxWebApp() can be false for a tick. A hard navigation here
          // would drop the hash fragment and strand the user on the Login Widget screen.
          const hash = typeof window !== 'undefined' ? window.location.hash : '';
          const looksLikeWebAppHash =
            hash.includes('tgWebAppData=') ||
            hash.includes('WebAppData=') ||
            (hash.includes('hash=') && hash.includes('auth_date='));
          if (
            !isTelegramWebApp() &&
            !isMaxWebApp() &&
            !looksLikeWebAppHash &&
            !window.location.pathname.includes('/auth')
          ) {
            window.location.href = '/auth/telegram';
          }
        }

        // On 412 `user_unverified`: actor (pending TgUser/MaxUser) tried to hit a verified-only
        // endpoint. Trigger the global SBP modal so the user can link their phone — but only
        // for user actions (non-GET); background page loads must let unlinked users browse.
        if (error.response?.status === 412) {
          const payload = error.response.data as ApiError | undefined;
          const method = (error.config?.method ?? 'get').toLowerCase();
          if (payload?.error === 'user_unverified' && method !== 'get') {
            sbpGateHandler?.();
          }
        }

        // On 423 `access_restricted`: user is under a temporary fraud ban.
        // Surface the global "Доступ ограничен" screen.
        if (error.response?.status === 423) {
          const payload = error.response.data as ApiError | undefined;
          if (payload?.error === 'access_restricted') {
            accessRestrictedHandler?.();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Health check
  async health(): Promise<{ status: string; timestamp: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // FAQ (public)
  async getFaq(): Promise<FaqItem[]> {
    const response = await this.client.get<ApiResponse<FaqItem[]>>('/faq');
    return response.data.data;
  }

  // Auth endpoints
  async verifyAuth(): Promise<AuthVerifyResponse> {
    try {
      const response = await this.client.get<AuthVerifyResponse>('/auth/verify');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle 401 specifically
        if (error.response?.status === 401) {
          return {
            success: false,
            authenticated: false,
            error: error.response?.data?.error || 'Authentication failed',
          };
        }
        throw error;
      }
      throw error;
    }
  }

  // User endpoints
  async getUserProfile(): Promise<User> {
    const response = await this.client.get<ApiResponse<User>>('/user/profile');
    return response.data.data;
  }

  // Onboarding Status endpoints
  async getOnboardingStatus(): Promise<OnboardingStatus> {
    const response = await this.client.get<OnboardingStatusResponse>('/user/onboarding-status');
    return response.data.data;
  }

  async completeOnboarding(skipped: boolean = false, data?: OnboardingStepData): Promise<OnboardingStatus> {
    const response = await this.client.post<OnboardingStatusResponse>('/user/complete-onboarding', {
      skipped,
      ...data
    });
    return response.data.data;
  }

  async resetOnboarding(): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean; data: OnboardingStatus; message: string }>('/user/reset-onboarding');
    return { success: response.data.success };
  }

  // Referral endpoints
  async getReferralInfo(): Promise<{ referral_info: any }> {
    const response = await this.client.get<{ success: boolean; referral_info: any }>('/user/referral-info');
    return response.data;
  }

  async getReferrals(page: number = 1, perPage: number = 10): Promise<any> {
    const response = await this.client.get(`/user/referrals?page=${page}&per_page=${perPage}`);
    return response.data;
  }

  // Stats endpoint
  async getStats(): Promise<any> {
    const response = await this.client.get('/user/stats');
    return response.data;
  }

  // Transactions endpoint
  async getTransactions(): Promise<{ success: boolean; data: Transaction[] }> {
    const response = await this.client.get<{ success: boolean; data: Transaction[] }>('/user/transactions');
    return response.data;
  }

  async getPenkiBalance(): Promise<number> {
    const response = await this.client.get<ApiResponse<{ balance: number }>>('/penki/balance');
    return response.data.data.balance;
  }

  async getPenkiTransactions(page: number = 1): Promise<PaginatedResponse<PenkiTransaction>> {
    const response = await this.client.get<PaginatedResponse<PenkiTransaction>>(`/penki/transactions?page=${page}`);
    return response.data;
  }

  async getPenkiClaimStatus(): Promise<PenkiClaimStatus> {
    const response = await this.client.get<ApiResponse<PenkiClaimStatus>>('/penki/claim-status');
    return response.data.data;
  }

  async claimPenki(): Promise<PenkiClaimResult> {
    const response = await this.client.post<ApiResponse<PenkiClaimResult>>('/penki/claim');
    return response.data.data;
  }

  async getPenkiRates(): Promise<PenkiRateRow[]> {
    const response = await this.client.get<ApiResponse<PenkiRateRow[]>>('/penki/rates');
    return response.data.data;
  }

  async getShopProducts(): Promise<ShopProduct[]> {
    const response = await this.client.get<ApiResponse<ShopProduct[]>>('/shop/products');
    return response.data.data;
  }

  async getShopProduct(id: number): Promise<ShopProduct> {
    const response = await this.client.get<ApiResponse<ShopProduct>>(`/shop/products/${id}`);
    return response.data.data;
  }

  async purchaseShopProduct(id: number, clientToken: string): Promise<ShopPurchase> {
    const response = await this.client.post<{ success: boolean; data: ShopPurchase }>(`/shop/products/${id}/purchase`, {
      client_token: clientToken,
    });
    return response.data.data;
  }

  async getShopPurchases(page: number = 1): Promise<PaginatedResponse<ShopPurchase>> {
    const response = await this.client.get<PaginatedResponse<ShopPurchase>>(`/shop/purchases?page=${page}&per_page=50`);
    return response.data;
  }

  async getShopPurchase(id: number): Promise<ShopPurchase> {
    const response = await this.client.get<ApiResponse<ShopPurchase>>(`/shop/purchases/${id}`);
    return response.data.data;
  }

  // Push settings endpoints
  async getPushSettings(): Promise<PushSettings> {
    const response = await this.client.get<ApiResponse<PushSettings>>('/pushes/settings');
    return response.data.data;
  }

  async updatePushSettings(settings: Partial<PushSettings>): Promise<PushSettings> {
    const response = await this.client.put<ApiResponse<PushSettings>>('/pushes/settings', settings);
    return response.data.data;
  }

  async togglePushSetting(type: string): Promise<PushSettings> {
    const response = await this.client.post<ApiResponse<PushSettings>>(`/pushes/settings/${type}/toggle`);
    return response.data.data;
  }

  // Character endpoints
  async getCharacters(): Promise<{ id: number; name: string }[]> {
    const response = await this.client.get<ApiResponse<{ id: number; name: string }[]>>('/characters');
    return response.data.data;
  }

  // NFT endpoints
  // The catalog response bundles the character directory (`characters`) so the
  // home page doesn't need a separate GET /characters request for its filter.
  async getNFTs(category?: string): Promise<{ data: NFTItem[]; characters?: { id: number; name: string }[] }> {
    let url = '/nfts';
    const params = new URLSearchParams();
    if (category && category !== 'all') params.append('category', category);
    
    if (params.toString()) {
      url += '?' + params.toString();
    }
    
    const response = await this.client.get<{ data: NFTItem[]; characters?: { id: number; name: string }[] }>(url);
    return response.data;
  }

  async getNFT(id: string): Promise<any> {
    const response = await this.client.get(`/nfts/${id}`);
    return response.data;
  }

  async purchaseNFT(id: string, clientToken?: string): Promise<NftPurchaseResponse> {
    const response = await this.client.post<ApiResponse<NftPurchaseResponse>>(
      `/nfts/${id}/purchase`,
      clientToken ? { client_token: clientToken } : undefined,
      { timeout: PAYMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data.data;
  }

  /**
   * Poll the backend for the current state of a purchase by its client token.
   * Always resolves; used to reliably recover the payment link (or a definitive
   * failure) even when the original /purchase response was lost.
   */
  async getPurchaseStatusByToken(clientToken: string): Promise<PurchaseTokenStatus> {
    const response = await this.client.get<ApiResponse<PurchaseTokenStatus>>(
      `/nfts/purchase-status/${encodeURIComponent(clientToken)}`,
      { timeout: PAYMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data.data;
  }

  // NFT Detail for modal
  async getNFTDetail(id: string): Promise<NFTDetailItem> {
    const response = await this.client.get<ApiResponse<NFTDetailItem>>(`/nfts/${id}/detail`);
    return response.data.data;
  }

  // Initiate NFT purchase (returns payment info)
  async initiateNFTPurchase(id: string, bankId?: string): Promise<PaymentInitResponse> {
    const response = await this.client.post<ApiResponse<PaymentInitResponse>>(
      `/nfts/${id}/initiate-purchase`,
      { bank_id: bankId },
      { timeout: PAYMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data.data;
  }

  async upgradeNFT(id: string, bankId?: string, clientToken?: string): Promise<NftPurchaseResponse> {
    const response = await this.client.post<ApiResponse<NftPurchaseResponse>>(
      `/nfts/${id}/upgrade`,
      { bank_id: bankId, ...(clientToken ? { client_token: clientToken } : {}) },
      { timeout: PAYMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data.data;
  }

  // Get random NFT for "Random NFT" feature
  // excludeOwned=true — только NFT, которыми пользователь не владеет
  async getRandomNFT(options?: { excludeOwned?: boolean }): Promise<NFTDetailItem> {
    const params = options?.excludeOwned ? '?exclude_owned=1' : '';
    const response = await this.client.get<ApiResponse<NFTDetailItem>>(`/nfts/random${params}`);
    return response.data.data;
  }

  // Reward NFTs endpoint
  async getRewardNFTs(options?: { limit?: number; shuffle?: boolean }): Promise<NFTItem[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.shuffle) params.append('shuffle', '1');
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await this.client.get<ApiResponse<NFTItem[]>>(`/nfts/reward${query}`);
    return response.data.data;
  }

  // User's successful purchases/sales
  async getMyPurchases(page: number = 1, sort: 'asc' | 'desc' = 'desc'): Promise<PaginatedResponse<NftPurchaseItem>> {
    const response = await this.client.get<PaginatedResponse<NftPurchaseItem>>(`/nfts/my-purchases?page=${page}&sort=${sort}`);
    return response.data;
  }

  // NFT sales progress for current user
  async getMyProgress(): Promise<{ sales_count: number; revenue: number; goal: number; active_total_value: number }> {
    const response = await this.client.get<ApiResponse<{ sales_count: number; revenue: number; goal: number; active_total_value: number }>>('/nfts/my-progress');
    return response.data.data;
  }

  // NFT stats
  async getNftStats(): Promise<{ total: number; available: number; max_step_price: number }> {
    const response = await this.client.get<ApiResponse<{ total: number; available: number; max_step_price: number }>>('/nfts/stats');
    return response.data.data;
  }

  // NFT ownership history
  async getNftOwnershipHistory(nftId: number): Promise<NFTOwnershipHistoryResponse> {
    const response = await this.client.get<ApiResponse<NFTOwnershipHistoryResponse>>(`/nfts/${nftId}/ownership-history`);
    return response.data.data;
  }

  // Preferences endpoints
  async getPreferences(): Promise<UserPreferences> {
    const response = await this.client.get<ApiResponse<UserPreferences>>('/user/preferences');
    return response.data.data;
  }

  async updatePreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    const response = await this.client.put<ApiResponse<UserPreferences>>('/user/preferences', preferences);
    return response.data.data;
  }

  // Language settings
  async updateLanguage(languageCode: string): Promise<{ success: boolean; language_code: string }> {
    const response = await this.client.put<{ success: boolean; language_code: string }>('/user/language', { language_code: languageCode });
    return response.data;
  }

  // SBP linking (legacy - simple phone save)
  async linkSBP(phone: string): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean; message: string }>('/user/link-sbp', { phone });
    return response.data;
  }

  async unlinkSBP(): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean; message: string }>('/user/unlink-sbp');
    return response.data;
  }

  // eKazna SBP linking
  async initiateSBPLinking(phone: string, bankId: string): Promise<{
    redirect_url: string | null;
  }> {
    const response = await this.client.post<{
      success: boolean;
      data: {
        redirect_url: string | null;
      };
      message?: string;
    }>('/ekazna-sbp/link', { phone, bank_id: bankId }, { timeout: PAYMENT_REQUEST_TIMEOUT_MS });
    return response.data.data;
  }

  async verifySBPCode(code: string, phone: string, bankId: string, bankName: string): Promise<{
    success: boolean;
    message: string;
    attempts_left?: number;
    locked_until?: number;
    remaining_seconds?: number;
  }> {
    const response = await this.client.post<{
      success: boolean;
      message: string;
      attempts_left?: number;
      locked_until?: number;
      remaining_seconds?: number;
    }>('/ekazna-sbp/verify-code', { code, phone, bank_id: bankId, bank_name: bankName });
    return response.data;
  }

  async resendSBPCode(phone: string): Promise<{
    success: boolean;
    message: string;
    resend_cooldown_seconds?: number;
  }> {
    const response = await this.client.post<{
      success: boolean;
      message: string;
      resend_cooldown_seconds?: number;
    }>('/ekazna-sbp/resend-code', { phone });
    return response.data;
  }

  // Max → Telegram account link (by phone + SMS)
  async sendMaxTgLinkCode(phone: string): Promise<{
    success: boolean;
    data?: { resend_cooldown_seconds?: number };
  }> {
    const response = await this.client.post<{
      success: boolean;
      data?: { resend_cooldown_seconds?: number };
    }>('/max/link-tg/send-code', { phone });
    return response.data;
  }

  async verifyMaxTgLinkCode(phone: string, code: string): Promise<{
    success: boolean;
    status: 'linked' | 'not_found' | 'invalid_code' | 'locked' | 'expired';
    message?: string;
    attempts_left?: number;
    locked_until?: number;
    remaining_seconds?: number;
    data?: { user?: User };
  }> {
    const response = await this.client.post<{
      success: boolean;
      status: 'linked' | 'not_found' | 'invalid_code' | 'locked' | 'expired';
      message?: string;
      attempts_left?: number;
      locked_until?: number;
      remaining_seconds?: number;
      data?: { user?: User };
    }>('/max/link-tg/verify-code', { phone, code });
    return response.data;
  }

  async resendMaxTgLinkCode(phone: string): Promise<{
    success: boolean;
    message?: string;
    resend_cooldown_seconds?: number;
    remaining_seconds?: number;
  }> {
    const response = await this.client.post<{
      success: boolean;
      message?: string;
      resend_cooldown_seconds?: number;
      remaining_seconds?: number;
    }>('/max/link-tg/resend-code', { phone });
    return response.data;
  }

  async dismissMaxTgLinkPrompt(): Promise<{ success: boolean }> {
    const response = await this.client.post<{ success: boolean }>('/max/link-tg/dismiss');
    return response.data;
  }

  // eKazna Payment status (paymentInfo)
  async getPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    const response = await this.client.get<ApiResponse<PaymentStatusResponse>>(
      `/ekazna-payment/status/${paymentId}`,
      { timeout: PAYMENT_REQUEST_TIMEOUT_MS }
    );
    return response.data.data;
  }

  // Bank directory bundled as static JSON (see public/data/c2bmembers.json)
  async fetchNSPKBanks(): Promise<Array<{
    bankName: string;
    logoURL: string;
    memberId: string;
  }>> {
    const response = await axios.get<{
      dictionary: Array<{
        bankName: string;
        logoURL: string;
        schema: string;
        package_name?: string;
      }>;
    }>('/data/c2bmembers.json');

    const mapped = response.data.dictionary.map((bank) => ({
      bankName: bank.bankName,
      logoURL: bank.logoURL,
      // Extract member ID from schema: "bank100000000111" -> "100000000111"
      memberId: bank.schema.replace(/^bank/, ''),
    }));

    // NSPK returns major banks first; keep that prefix, sort the rest A–Z (ru)
    const NSPK_POPULAR_PREFIX_COUNT = 15;
    const popular = mapped.slice(0, NSPK_POPULAR_PREFIX_COUNT);
    const rest = mapped.slice(NSPK_POPULAR_PREFIX_COUNT).sort((a, b) =>
      a.bankName.localeCompare(b.bankName, 'ru', { sensitivity: 'base' })
    );
    return [...popular, ...rest];
  }

  // Profile update
  async updateProfile(data: { first_name?: string; last_name?: string; email?: string }): Promise<{ success: boolean; data: { first_name: string; last_name: string; username: string; email: string } }> {
    const response = await this.client.put<{ success: boolean; data: { first_name: string; last_name: string; username: string; email: string }; message: string }>('/user/profile', data);
    return response.data;
  }

  // White List endpoints
  async getWhiteListStatus(): Promise<{ in_white_list: boolean; nft_id?: number; joined_at?: string }> {
    const response = await this.client.get<ApiResponse<{ in_white_list: boolean; nft_id?: number; joined_at?: string }>>('/white-list/status');
    return response.data.data;
  }

  async getWhiteListStats(): Promise<{ total_spots: number; spots_taken: number; remaining_spots: number; white_list_start_date: string; opendoor_start_date: string | null; is_late_user: boolean }> {
    const response = await this.client.get<ApiResponse<{ total_spots: number; spots_taken: number; remaining_spots: number; white_list_start_date: string; opendoor_start_date: string | null; is_late_user: boolean }>>('/white-list/stats');
    return response.data.data;
  }

  /**
   * Returns redirect path after onboarding: '/claim-reward' if white list is still active,
   * '/' (home) if open door has started.
   */
  async getPostOnboardingRedirectPath(): Promise<'/claim-reward' | '/'> {
    try {
      const stats = await this.getWhiteListStats();
      const opendoorStartDate = stats?.opendoor_start_date ? new Date(stats.opendoor_start_date) : null;
      if (opendoorStartDate && new Date() >= opendoorStartDate) {
        return '/';
      }
      return '/claim-reward';
    } catch {
      return '/claim-reward';
    }
  }

  async claimWhiteListReward(nftId: string): Promise<{ success: boolean; nft_id?: number; already_claimed?: boolean; message?: string }> {
    const response = await this.client.post<{ success: boolean; data?: { nft_id: number; already_claimed: boolean }; message: string }>('/white-list/claim-reward', { nft_id: parseInt(nftId, 10) });
    return {
      success: response.data.success,
      nft_id: response.data.data?.nft_id,
      already_claimed: response.data.data?.already_claimed,
      message: response.data.message,
    };
  }

  // User NFT endpoints (My NFTs)
  async getMyNfts(): Promise<MyNFTsResponse> {
    const response = await this.client.get<ApiResponse<MyNFTsResponse>>('/my-nfts');
    return response.data.data;
  }

  async getMySoldNfts(): Promise<SoldNFTsResponse> {
    const response = await this.client.get<ApiResponse<SoldNFTsResponse>>('/my-nfts/sold');
    return response.data.data;
  }

  async getNftDetail(id: number): Promise<UserNFTDetail> {
    const response = await this.client.get<ApiResponse<UserNFTDetail>>(`/my-nfts/${id}`);
    return response.data.data;
  }

  async markNftViewed(id: number): Promise<{ success: boolean; badges_removed: boolean }> {
    const response = await this.client.post<{ success: boolean; badges_removed: boolean }>(`/my-nfts/${id}/view`);
    return response.data;
  }

  async withdrawNft(id: number): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>(`/my-nfts/${id}/withdraw`);
    return response.data;
  }

  async claimNftPrize(id: number): Promise<ClaimPrizeResponse> {
    const response = await this.client.post<ClaimPrizeResponse>(`/my-nfts/${id}/claim-prize`);
    return response.data;
  }

  async getPrizeStatus(id: number): Promise<PrizeStatusResponse> {
    const response = await this.client.get<PrizeStatusResponse>(`/my-nfts/${id}/prize-status`);
    return response.data;
  }

  async getClaimedPrizes(): Promise<{ data: ClaimedPrize[]; total: number }> {
    const response = await this.client.get<{ data: ClaimedPrize[]; total: number }>('/my-nfts/claimed-prizes');
    return response.data;
  }

  async getClaimedPrizeDetail(id: number): Promise<ClaimedPrize> {
    const response = await this.client.get<ApiResponse<ClaimedPrize>>(`/my-nfts/claimed-prizes/${id}`);
    return response.data.data;
  }

  // Prize Drop endpoints
  async getCurrentDrop(): Promise<DropCurrentResponse> {
    const response = await this.client.get<ApiResponse<DropCurrentResponse>>('/drops/current');
    return response.data.data;
  }

  async getMyDrops(): Promise<MyDropsResponse> {
    const response = await this.client.get<ApiResponse<MyDropsResponse>>('/drops/my-drops');
    return response.data.data;
  }

  async getTierInfo(): Promise<TierInfoResponse> {
    const response = await this.client.get<ApiResponse<TierInfoResponse>>('/drops/tier-info');
    return response.data.data;
  }

  // Support ticket endpoints
  async getSupportTickets(): Promise<SupportTicket[]> {
    const response = await this.client.get<{ success: boolean; data: SupportTicket[] }>('/support/tickets');
    return response.data.data;
  }

  async getSupportUnread(): Promise<boolean> {
    const response = await this.client.get<{ success: boolean; data: { has_unread: boolean } }>('/support/tickets/unread');
    return response.data.data.has_unread;
  }

  async createSupportTicket(subject: string, message: string): Promise<SupportTicketDetail> {
    const response = await this.client.post<{ success: boolean; data: SupportTicketDetail }>('/support/tickets', { subject, message });
    return response.data.data;
  }

  async getSupportTicket(id: number): Promise<SupportTicketDetail> {
    const response = await this.client.get<{ success: boolean; data: SupportTicketDetail }>(`/support/tickets/${id}`);
    return response.data.data;
  }

  async sendSupportMessage(ticketId: number, message: string): Promise<SupportMessage> {
    const response = await this.client.post<{ success: boolean; data: SupportMessage }>(`/support/tickets/${ticketId}/messages`, { message });
    return response.data.data;
  }

  /** One call per browser tab session — backend records a Mini App launch for admin analytics. */
  async trackMiniappOpen(): Promise<void> {
    await this.client.post('/miniapp/track-open');
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

/**
 * Map any request failure to a user-facing Russian message that includes a
 * visible status/error code. The code is intentionally rendered on screen so a
 * screenshot from a user is enough to tell apart an expired session (401),
 * a block (403), a timeout, a network drop, or a server error.
 */
export const describeApiError = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return 'Что-то пошло не так. Попробуйте позже.';
  }

  const axiosError = error as AxiosError<ApiError>;

  // No HTTP response reached us: timeout or network failure.
  if (!axiosError.response) {
    if (axiosError.code === 'ECONNABORTED') {
      return 'Превышено время ожидания. Проверьте соединение и попробуйте снова. (таймаут)';
    }
    return 'Нет связи с сервером. Проверьте интернет и попробуйте снова. (сеть)';
  }

  const status = axiosError.response.status;

  switch (status) {
    case 401:
      return 'Сессия устарела. Закройте и снова откройте приложение. (код 401)';
    case 403:
      return 'Доступ ограничен. Обратитесь в поддержку. (код 403)';
    case 423:
      return 'Доступ ограничен.';
    case 429:
      return 'Слишком много запросов. Немного подождите и попробуйте снова. (код 429)';
  }

  if (status >= 500) {
    return `Ошибка сервера. Попробуйте позже. (код ${status})`;
  }

  return `Не удалось загрузить данные. Попробуйте позже. (код ${status})`;
};

// Backward-compatible alias.
export const handleApiError = (error: AxiosError<ApiError>): string => describeApiError(error);
